import React, {
    createContext,
    useState,
    useContext,
    ReactNode,
    useRef
} from 'react';
import {IImage} from '../../types';

interface IVideoBannerContextType {
    totalMilliseconds: number;
    setTotalMilliseconds: (ms: number) => void;
    isPlayedOrSkiped: boolean;
    setIsPlayedOrSkiped: React.Dispatch<React.SetStateAction<boolean>>;
    isPlaying: boolean;
    setIsPlaying: (playing: boolean) => void;
    playedProgress: number;
    setPlayedProgress: (progress: number) => void;
    bufferedProgress: number;
    setBufferedProgress: (progress: number) => void;
    isMuted: boolean;
    setIsMuted: (muted: boolean) => void;
    isDragging: boolean;
    setIsDragging: (dragging: boolean) => void;
    isLoaded: boolean;
    setIsLoaded: (loaded: boolean) => void;
    isFullScreen: boolean;
    setIsFullScreen: (fullScreen: boolean) => void;
    currentTime: string;
    setCurrentTime: (time: string) => void;
    totalDuration: string;
    setTotalDuration: (duration: string) => void;
    thumbnail: IImage | undefined;
    setThumbnail: (thumbnail: IImage | undefined) => void;
    isHovered: boolean;
    setIsHovered: (isHovered: boolean) => void;
    showModal: boolean;
    setShowModal: (showModal: boolean) => void;
    stateResetter: () => void;
    videoRef: React.RefObject<HTMLVideoElement>;
    screenRef: React.RefObject<HTMLDivElement>;
}

const VideoBannerContext = createContext<IVideoBannerContextType | undefined>(
    undefined
);

export const useVideoBannerContext = () => {
    const context = useContext(VideoBannerContext);
    if (!context) {
        throw new Error(
            'useVideoBannerContext must be used within a VideoBannerContextProvider'
        );
    }
    return context;
};

interface VideoBannerProviderProps {
    children: ReactNode;
}

export const VideoBannerContextProvider: React.FC<VideoBannerProviderProps> = ({
    children
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const screenRef = useRef<HTMLDivElement>(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [isPlayedOrSkiped, setIsPlayedOrSkiped] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const [playedProgress, setPlayedProgress] = useState(0);
    const [bufferedProgress, setBufferedProgress] = useState(0);
    const [totalMilliseconds, setTotalMilliseconds] = useState(0);

    const [currentTime, setCurrentTime] = useState('00:00');
    const [totalDuration, setTotalDuration] = useState('00:00');

    const [thumbnail, setThumbnail] = useState<IImage>();

    // Store setters and boolean states dynamically
    const booleanStateSetters = {
        isPlaying: setIsPlaying,
        isMuted: setIsMuted,
        isDragging: setIsDragging,
        isLoaded: setIsLoaded,
        isFullScreen: setIsFullScreen,
        isHovered: setIsHovered,
        showModal: setShowModal
    };

    // Store setters and number states dynamically
    const numberStateSetters = {
        playedProgress: setPlayedProgress,
        bufferedProgress: setBufferedProgress,
        totalMilliseconds: setTotalMilliseconds
    };

    // Store setters and string states dynamically
    const timerStateSetters = {
        currentTime: setCurrentTime,
        totalDuration: setTotalDuration
    };

    /**
     * Reset all states dynamically, with an optional override.
     * @param excludeKeys - An array of keys to exclude from resetting.
     */
    const stateResetter = (
        excludeKeys: (
            | keyof typeof booleanStateSetters
            | keyof typeof numberStateSetters
            | keyof typeof timerStateSetters
        )[] = []
    ) => {
        Object.entries(booleanStateSetters).forEach(([key, setter]) => {
            if (
                !excludeKeys.includes(key as keyof typeof booleanStateSetters)
            ) {
                setter(false); // Reset the state if it's not excluded
            }
        });
        Object.entries(numberStateSetters).forEach(([key, setter]) => {
            if (!excludeKeys.includes(key as keyof typeof numberStateSetters)) {
                setter(0); // Reset the state if it's not excluded
            }
        });
        Object.entries(timerStateSetters).forEach(([key, setter]) => {
            if (!excludeKeys.includes(key as keyof typeof timerStateSetters)) {
                setter('00:00'); // Reset the state if it's not excluded
            }
        });
    };

    return (
        <VideoBannerContext.Provider
            value={{
                totalMilliseconds,
                setTotalMilliseconds,
                isPlaying,
                setIsPlaying,
                playedProgress,
                setPlayedProgress,
                bufferedProgress,
                setBufferedProgress,
                isMuted,
                setIsMuted,
                isDragging,
                setIsDragging,
                isLoaded,
                setIsLoaded,
                currentTime,
                setCurrentTime,
                totalDuration,
                setTotalDuration,
                videoRef,
                screenRef,
                thumbnail,
                setThumbnail,
                isFullScreen,
                setIsFullScreen,
                isHovered,
                setIsHovered,
                showModal,
                setShowModal,
                stateResetter,
                isPlayedOrSkiped,
                setIsPlayedOrSkiped
            }}
        >
            {children}
        </VideoBannerContext.Provider>
    );
};
